// ----------------------------------------------------------------------------
// Section: Ingredients
// ----------------------------------------------------------------------------
//
// Hosts Ingredient blocks (src/blocks/ingredient.liquid) as a single-open
// accordion (reuses the generic `js-accordion` component + its
// `data-collapse-all` and fragment/hash-open behaviour).
//
// Each Ingredient block renders its own media chunk (it's the only place
// that can reliably read that block's own image/video settings) but it
// belongs in the section's shared media slot, not inline in the row. On
// init this script moves every block's media chunk into
// `[data-ingredients-media]`, then keeps the slot in sync with whichever
// ingredient is active — reading only the live DOM, never another block's
// Liquid settings.
//
// It also enforces "only one block stays open on load" against the DOM:
// each block resolves its own open/closed state independently in Liquid
// (a block can't reliably read a sibling's settings either), so more than
// one can start open until this reconciles it — preferring the block
// targeted by the URL fragment, if any.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import 'snippets/_containers/accordion/accordion';
import './_ingredients.scss';

// -------------------------------------
// Variables
// -------------------------------------

var SECTION_SELECTOR = '[data-section-type="ingredients"]';
var ACCORDION_SELECTOR = '.js-accordion';
var ITEM_SELECTOR = '.js-accordion__item[data-ingredient-item]';
var MEDIA_CONTAINER_SEL = '[data-ingredients-media]';
var MEDIA_ITEM_SELECTOR = '[data-ingredient-media]';
var PLAYPAUSE_SELECTOR = '[data-media-playpause]';
var ACTIVE_CLASS = 'is-active';
var PAUSED_CLASS = 'is-paused';

// -------------------------------------
// Helpers
// -------------------------------------

function closeItem(item) {
  var button = item.querySelector('.js-accordion__button');
  var content = item.querySelector('.js-accordion__content');
  item.classList.remove('is-open');
  if (button) {
    button.classList.remove('is-open');
    button.setAttribute('aria-expanded', 'false');
  }
  if (content) {
    content.classList.remove('is-open');
    content.hidden = true;
    content.style.height = null;
  }
}

// Each block resolves its own open state independently in Liquid, so more
// than one can render open. Keep only the fragment-targeted item (if the
// hash already opened one via the accordion component) or the first open
// item in DOM order, matching "only the first Default Open block opens".
function enforceSingleOpen(accordion) {
  var openItems = Array.from(accordion.querySelectorAll("".concat(ITEM_SELECTOR, ".is-open")));
  if (openItems.length <= 1) {
    return;
  }
  var hash = window.location.hash.slice(1);
  var keepItem = hash && openItems.find(item => item.id === hash) || openItems[0];
  openItems.forEach(item => {
    if (item !== keepItem) {
      closeItem(item);
    }
  });
}

// Moves each Ingredient block's own media chunk out of its row and into the
// section's shared media slot, tagging it with its parent item's id so the
// active-item lookup below can find it again.
function collectMedia(section, mediaContainer) {
  section.querySelectorAll(ITEM_SELECTOR).forEach(item => {
    var media = item.querySelector(MEDIA_ITEM_SELECTOR);
    if (media) {
      mediaContainer.appendChild(media);
    }
  });
}
function resolveActiveMediaItem(section, mediaContainer) {
  var openItem = section.querySelector("".concat(ITEM_SELECTOR, ".is-open"));
  var targetId = openItem ? openItem.id : null;
  var target = targetId ? mediaContainer.querySelector("[data-media-for=\"".concat(targetId, "\"]")) : null;

  // Fall back to whichever ingredient's media renders first in the slot
  // (the open block configured none, or nothing is open).
  return target || mediaContainer.querySelector(MEDIA_ITEM_SELECTOR);
}
function stopMedia(item) {
  if (!item) {
    return;
  }
  var video = item.querySelector('video');
  if (video) {
    video.pause();
    video.currentTime = 0;
  }
  item.hidden = true;
  item.classList.remove(ACTIVE_CLASS, PAUSED_CLASS);
}
function playMedia(item) {
  if (!item) {
    return;
  }
  item.hidden = false;
  item.classList.add(ACTIVE_CLASS);
  item.classList.remove(PAUSED_CLASS);
  var video = item.querySelector('video');
  var button = item.querySelector(PLAYPAUSE_SELECTOR);
  var label = item.querySelector('[data-media-playpause-label]');
  if (button) {
    button.setAttribute('aria-label', button.dataset.labelPause);
    if (label) label.textContent = button.dataset.labelPause;
  }
  if (video) {
    video.currentTime = 0;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  }
  positionPlayPause(item);
}

// On desktop (list and media side by side) the media slot matches the
// list's height as it stands on load, then stays pinned there — only the
// list is allowed to grow/shrink as rows open and close. On mobile (stacked)
// the media slot keeps its own fixed aspect ratio from CSS instead.
function syncMediaHeight(section) {
  var layout = section.querySelector('.ingredients__layout');
  var list = section.querySelector('.ingredients__list');
  var media = section.querySelector(MEDIA_CONTAINER_SEL);
  if (!layout || !list || !media) {
    return;
  }

  // Media position: Left renders as "row-reverse", not "row" — both count
  // as side by side.
  var flexDirection = window.getComputedStyle(layout).flexDirection;
  var isSideBySide = flexDirection === 'row' || flexDirection === 'row-reverse';
  media.style.height = isSideBySide ? "".concat(list.offsetHeight, "px") : '';
}

// With object-fit: cover the video always fills its box, so the button's
// fixed CSS inset already sits on the visible video edge. With contain the
// video letterboxes (empty space on two sides) — the CSS inset would then
// float in that empty space instead of on the video, so this measures the
// actual rendered video box and re-anchors the button to its real corner.
function positionPlayPause(item) {
  if (!item) {
    return;
  }
  var video = item.querySelector('video');
  var button = item.querySelector(PLAYPAUSE_SELECTOR);
  if (!video || !button) {
    return;
  }
  var isContain = window.getComputedStyle(video).objectFit === 'contain';
  var videoWidth = video.videoWidth;
  var videoHeight = video.videoHeight;
  var boxWidth = video.clientWidth;
  var boxHeight = video.clientHeight;
  if (!isContain || !videoWidth || !videoHeight || !boxWidth || !boxHeight) {
    button.style.right = '';
    button.style.bottom = '';
    return;
  }
  var inset = 16;
  var scale = Math.min(boxWidth / videoWidth, boxHeight / videoHeight);
  var letterboxX = (boxWidth - videoWidth * scale) / 2;
  var letterboxY = (boxHeight - videoHeight * scale) / 2;
  button.style.right = "".concat(letterboxX + inset, "px");
  button.style.bottom = "".concat(letterboxY + inset, "px");
}
function initVideoPositioning(mediaContainer) {
  mediaContainer.querySelectorAll(MEDIA_ITEM_SELECTOR).forEach(function (item) {
    var video = item.querySelector('video');
    if (!video) {
      return;
    }
    video.addEventListener('loadedmetadata', function () {
      positionPlayPause(item);
    });
  });
}
function updateMedia(section, mediaContainer) {
  var nextItem = resolveActiveMediaItem(section, mediaContainer);
  var currentItem = mediaContainer.querySelector(".".concat(ACTIVE_CLASS));
  if (nextItem === currentItem) {
    return;
  }
  stopMedia(currentItem);
  playMedia(nextItem);
}
function initPlayPause(mediaContainer) {
  mediaContainer.querySelectorAll(PLAYPAUSE_SELECTOR).forEach(function (button) {
    button.addEventListener('click', function () {
      var item = button.closest(MEDIA_ITEM_SELECTOR);
      var video = item ? item.querySelector('video') : null;
      var label = item ? item.querySelector('[data-media-playpause-label]') : null;
      if (!video) {
        return;
      }
      if (video.paused) {
        video.play();
        item.classList.remove(PAUSED_CLASS);
        button.setAttribute('aria-label', button.dataset.labelPause);
        if (label) label.textContent = button.dataset.labelPause;
      } else {
        video.pause();
        item.classList.add(PAUSED_CLASS);
        button.setAttribute('aria-label', button.dataset.labelPlay);
        if (label) label.textContent = button.dataset.labelPlay;
      }
    });
  });
}
function initSection(section) {
  var mediaContainer = section.querySelector(MEDIA_CONTAINER_SEL);
  var accordion = section.querySelector(ACCORDION_SELECTOR);
  if (!mediaContainer) {
    return;
  }
  collectMedia(section, mediaContainer);
  initPlayPause(mediaContainer);
  initVideoPositioning(mediaContainer);
  if (accordion) {
    enforceSingleOpen(accordion);
  }
  updateMedia(section, mediaContainer);
  syncMediaHeight(section);
  var resizeTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      syncMediaHeight(section);
      mediaContainer.querySelectorAll(MEDIA_ITEM_SELECTOR).forEach(positionPlayPause);
    }, 150);
  });
  if (!accordion) {
    return;
  }

  // Catches both click-driven toggles and the accordion's own hash-open
  // behaviour, so the media slot always reflects whichever item is open.
  // Deliberately does NOT re-run syncMediaHeight — the media slot must
  // stay pinned at its on-load height while rows open and close.
  var observer = new MutationObserver(function () {
    updateMedia(section, mediaContainer);
  });
  observer.observe(accordion, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true
  });
}
function initIngredients() {
  var container = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  initIngredients();
});
document.addEventListener('shopify:section:load', _ref => {
  var {
    target
  } = _ref;
  initIngredients(target);
});